import { Link } from "react-router-dom";
import { Lock, CalendarClock } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { fmtDate } from "@/lib/dateFormat";

/**
 * Shown app-wide when the garage's subscription is cancelled/ending.
 * - read_only: the account is view-only; writes are blocked server-side.
 * - cancel_at_period_end (still full): a heads-up that access ends on the date.
 * Suspension is handled separately (hard lock-out), not here.
 */
export function ReadOnlyBanner() {
  const { user } = useAuth();
  if (!user) return null;

  if (user.read_only) {
    return (
      <div
        data-testid="readonly-banner"
        className="flex flex-wrap items-center gap-3 border-b border-red-300 bg-red-50 px-4 py-2.5 text-sm text-red-900 sm:px-8"
      >
        <Lock className="h-4 w-4 shrink-0" />
        <span className="min-w-[180px] flex-1">
          Your subscription is cancelled, so your account is <b>read-only</b>. You can view your
          existing data, but creating, editing and processing records is disabled. Reactivate to
          restore full access.
        </span>
        <Link
          to="/billing"
          data-testid="readonly-reactivate-btn"
          className="inline-flex items-center gap-2 rounded-md bg-red-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-red-700"
        >
          Reactivate subscription
        </Link>
      </div>
    );
  }

  if (user.cancel_at_period_end) {
    return (
      <div
        data-testid="ending-banner"
        className="flex flex-wrap items-center gap-3 border-b border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-900 sm:px-8"
      >
        <CalendarClock className="h-4 w-4 shrink-0" />
        <span className="min-w-[180px] flex-1">
          Your subscription is scheduled to cancel
          {user.current_period_end ? ` on ${fmtDate(user.current_period_end)}` : " at the end of the billing period"}.
          You keep full access until then, after which the account becomes read-only.
        </span>
        <Link
          to="/billing"
          data-testid="ending-resume-btn"
          className="inline-flex items-center gap-2 rounded-md bg-amber-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-amber-700"
        >
          Keep my subscription
        </Link>
      </div>
    );
  }

  return null;
}
